import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import {
  HomeHero,
  ResultsStrip,
  WorkStories,
  ServicesPricing,
  VerticalTestimonials,
  HowWeWork,
  MeetTheTeam,
  VibeCheck,
  HomeFaq,
} from './components/home';
import Footer from './components/Footer';
import usePageTracking from './hooks/usePageTracking';
import { useRouteMeta } from './seo';
import MobileCtaBar from './components/MobileCtaBar';
import ScrollToTop from './components/ScrollToTop';
import PageTransition from './components/PageTransition';
import RevealScope from './components/RevealScope';
import CaseStudyScrollProgress from './components/CaseStudyScrollProgress';
import { VIDEO_PORTFOLIO_ENABLED } from './config/features';
import { isWithHost, WITH_ORIGIN } from './with/config';
import { SITE_URL } from './seo';

const OurStory = lazy(() => import('./pages/OurStory'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Work = lazy(() => import('./pages/Work'));
const BrandCaseStudyPage = lazy(() => import('./pages/work/[slug].jsx'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const Contact = lazy(() => import('./pages/Contact'));
const WorkWithUs = lazy(() => import('./pages/WorkWithUs'));
const JobApplication = lazy(() => import('./pages/JobApplication'));
const BookCall = lazy(() => import('./pages/BookCall'));
const Services = lazy(() => import('./pages/Services'));
const PartnerWithUs = lazy(() => import('./pages/PartnerWithUs'));
const GalleryAll = lazy(() => import('./pages/GalleryAll'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminEvents = lazy(() => import('./pages/AdminEvents'));
const AdminBlogs = lazy(() => import('./pages/AdminBlogs'));
const AdminReviews = lazy(() => import('./pages/AdminReviews'));
const AdminPositions = lazy(() => import('./pages/AdminPositions'));
const AdminApplications = lazy(() => import('./pages/AdminApplications'));
const AdminWork = lazy(() => import('./pages/AdminWork'));
const AdminCaseStudies = lazy(() => import('./pages/AdminCaseStudies'));
const AdminGallery = lazy(() => import('./pages/AdminGallery'));
const AdminTeam = lazy(() => import('./pages/AdminTeam'));
const Videos = lazy(() => import('./pages/Videos'));
const AdminVideos = lazy(() => import('./pages/AdminVideos'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const NotFound = lazy(() => import('./pages/NotFound'));
const GraphicPortfolio = lazy(() => import('./pages/GraphicPortfolio'));
const WebsitePortfolio = lazy(() => import('./pages/WebsitePortfolio'));
const DesignPortfolio = lazy(() => import('./pages/DesignPortfolio'));
const AdminGraphicPortfolio = lazy(() => import('./pages/AdminGraphicPortfolio'));
const AdminWith = lazy(() => import('./pages/AdminWith'));

// With.apnaaapan: community sub-site with its own layout under /with
const WithLayout = lazy(() => import('./with/WithLayout'));
const WithHome = lazy(() => import('./with/pages/Home'));
const WithCommunity = lazy(() => import('./with/pages/Community'));
const WithCreators = lazy(() => import('./with/pages/Creators'));
const WithOpportunities = lazy(() => import('./with/pages/Opportunities'));
const WithOpportunityDetail = lazy(() => import('./with/pages/OpportunityDetail'));
const WithBrands = lazy(() => import('./with/pages/Brands'));
const WithBrandEnquiry = lazy(() => import('./with/pages/BrandEnquiry'));
const WithStory = lazy(() => import('./with/pages/Story'));
const WithJoin = lazy(() => import('./with/pages/Join'));
const WithCreatorApply = lazy(() => import('./with/pages/CreatorApply'));
const WithContact = lazy(() => import('./with/pages/Contact'));
const WithEventDetail = lazy(() => import('./with/pages/EventDetail'));
const WithManage = lazy(() => import('./with/pages/Manage'));
const WithAccount = lazy(() => import('./with/pages/Account'));
const WithSetPassword = lazy(() => import('./with/pages/Account').then((m) => ({ default: m.WithSetPassword })));
const WithPeople = lazy(() => import('./with/pages/People'));
const WithPerson = lazy(() => import('./with/pages/People').then((m) => ({ default: m.WithPerson })));
const WithPrivacy = lazy(() => import('./with/pages/Legal').then((m) => ({ default: m.WithPrivacy })));
const WithTerms = lazy(() => import('./with/pages/Legal').then((m) => ({ default: m.WithTerms })));
const WithNotFound = lazy(() => import('./with/pages/Legal').then((m) => ({ default: m.WithNotFound })));

export const isWithPath = (pathname) => pathname === '/with' || pathname.startsWith('/with/');

const ON_WITH_HOST = isWithHost();

/** Sends the browser to another host (keeps path and query). Used for cross-host fallbacks. */
function HostRedirect({ to }) {
  React.useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <div className="min-h-screen" aria-busy="true" />;
}

function App() {
  const location = useLocation();
  const isCaseStudyPage = /^\/work\/[^/]+$/.test(location.pathname);

  // Route meta must be set before the page view is tracked so titles are correct
  useRouteMeta();
  usePageTracking();

  // On with.apnaaapan.com: the admin stays on www.apnaaapan.com, and old /with/* links drop the prefix.
  if (ON_WITH_HOST && location.pathname.startsWith('/admin')) {
    return <HostRedirect to={`${SITE_URL}${location.pathname}${location.search}`} />;
  }
  if (ON_WITH_HOST && isWithPath(location.pathname)) {
    return <Navigate to={`${location.pathname.replace(/^\/with/, '') || '/'}${location.search}${location.hash}`} replace />;
  }
  // On apnaaapan.com in production, /with/* belongs to the subdomain (Nginx/Vercel normally redirect first).
  if (!ON_WITH_HOST && isWithPath(location.pathname) && /(^|\.)apnaaapan\.com$/i.test(window.location.hostname)) {
    return <HostRedirect to={`${WITH_ORIGIN}${location.pathname.replace(/^\/with/, '') || '/'}${location.search}${location.hash}`} />;
  }

  if (ON_WITH_HOST || isWithPath(location.pathname)) {
    return (
      <>
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen bg-with-paper" aria-busy="true" />}>
          <Routes>
            <Route path={ON_WITH_HOST ? '/' : '/with'} element={<WithLayout />}>
              <Route index element={<WithHome />} />
              <Route path="community" element={<WithCommunity />} />
              <Route path="creators" element={<WithCreators />} />
              <Route path="opportunities" element={<WithOpportunities />} />
              <Route path="opportunities/:slug" element={<WithOpportunityDetail />} />
              <Route path="brands" element={<WithBrands />} />
              <Route path="brands/enquiry" element={<WithBrandEnquiry />} />
              <Route path="story" element={<WithStory />} />
              <Route path="join" element={<WithJoin />} />
              <Route path="apply" element={<WithCreatorApply />} />
              <Route path="contact" element={<WithContact />} />
              <Route path="events/:slug" element={<WithEventDetail />} />
              <Route path="manage" element={<WithManage />} />
              <Route path="account" element={<WithAccount />} />
              <Route path="account/set-password" element={<WithSetPassword />} />
              <Route path="people" element={<WithPeople />} />
              <Route path="people/:slug" element={<WithPerson />} />
              <Route path="privacy" element={<WithPrivacy />} />
              <Route path="terms" element={<WithTerms />} />
              <Route path="*" element={<WithNotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#EFE7D5]">
      <ScrollToTop />
      {isCaseStudyPage ? <CaseStudyScrollProgress /> : null}
      <Header progressBarOffset={isCaseStudyPage} />
      <div className="pt-14 sm:pt-16 md:pt-20">
        <PageTransition>
        <Suspense fallback={<div className="min-h-[60vh]" aria-busy="true" />}>
        <Routes>
          {/* Home Page */}
          <Route
            path="/"
            element={
              <RevealScope>
                {/* Show, don't explain: reels, results and work come before process */}
                <HomeHero />
                <ResultsStrip />
                <WorkStories />
                <ServicesPricing />
                <VerticalTestimonials />
                <HowWeWork />
                <MeetTheTeam />
                <HomeFaq />
                <VibeCheck />
              </RevealScope>
            }
          />

          {/* Other Pages */}
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/work" element={<Work />} />
          <Route path="/work/:slug" element={<BrandCaseStudyPage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/work-with-us" element={<WorkWithUs />} />
          <Route path="/work-with-us/apply/:positionId" element={<JobApplication />} />
          <Route path="/book-call" element={<BookCall />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<Services />} />
          <Route path="/partner-with-us" element={<PartnerWithUs />} />
          <Route path="/gallery" element={<GalleryAll />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsConditions />} />
          {/* The old single community page now lives at /with */}
          <Route path="/with-apnaaapan" element={<Navigate to="/with" replace />} />
          {/* Admin Panel: Dashboard + sections */}
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/blogs" element={<AdminBlogs />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
          <Route path="/admin/positions" element={<AdminPositions />} />
          <Route path="/admin/applications" element={<AdminApplications />} />
          <Route path="/admin/work" element={<AdminWork />} />
          <Route path="/admin/case-studies" element={<AdminCaseStudies />} />
          <Route path="/admin/gallery" element={<AdminGallery />} />
          <Route path="/admin/events" element={<AdminEvents />} />
          <Route path="/admin/team" element={<AdminTeam />} />
          <Route path="/admin/with" element={<AdminWith />} />
          {VIDEO_PORTFOLIO_ENABLED ? (
            <>
              <Route path="/portfolio" element={<Videos />} />
              <Route path="/admin/portfolio" element={<AdminVideos />} />
              <Route path="/videos" element={<Navigate to="/portfolio" replace />} />
              <Route path="/admin/videos" element={<Navigate to="/admin/portfolio" replace />} />
            </>
          ) : (
            <>
              <Route path="/portfolio" element={<Navigate to="/" replace />} />
              <Route path="/videos" element={<Navigate to="/" replace />} />
              <Route path="/admin/portfolio" element={<Navigate to="/admin" replace />} />
              <Route path="/admin/videos" element={<Navigate to="/admin" replace />} />
            </>
          )}
          <Route path="/graphic-portfolio" element={<GraphicPortfolio />} />
          <Route path="/website-portfolio" element={<WebsitePortfolio />} />
          <Route path="/design-portfolio" element={<DesignPortfolio />} />
          <Route path="/admin/graphic-portfolio" element={<AdminGraphicPortfolio />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </PageTransition>
        <Footer />
      </div>
      {location.pathname.startsWith('/admin') ? null : <MobileCtaBar />}
    </div>
  );
}

export default App; 